import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CollaborationPanel = ({ 
  activeUsers, 
  recentChanges, 
  suggestions,
  onInviteUser,
  onAcceptSuggestion,
  onRejectSuggestion
}) => {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('editor');

  const handleInvite = () => {
    if (inviteEmail?.trim()) {
      onInviteUser(inviteEmail, inviteRole);
      setInviteEmail('');
      setShowInviteModal(false);
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="bg-card border-l border-border w-72 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-medium text-foreground">Collaboration</h3>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowInviteModal(true)}
            iconName="UserPlus"
            iconSize={16}
          />
        </div>

        {/* Active Users */}
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">Active now ({activeUsers?.length})</p>
          <div className="flex -space-x-2">
            {activeUsers?.slice(0, 5)?.map((user) => (
              <div
                key={user?.id}
                className="w-8 h-8 rounded-full border-2 border-background flex items-center justify-center text-xs font-medium"
                style={{ backgroundColor: user?.color }}
                title={user?.name}
              >
                {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
              </div>
            ))}
            {activeUsers?.length > 5 && (
              <div className="w-8 h-8 bg-muted rounded-full border-2 border-background flex items-center justify-center text-xs font-medium">
                +{activeUsers?.length - 5}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Content Tabs */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          {/* Recent Changes */}
          <div className="p-4 border-b border-border">
            <h4 className="font-medium text-foreground mb-3">Recent Changes</h4>
            <div className="space-y-3 max-h-40 overflow-y-auto">
              {recentChanges?.map((change) => (
                <div key={change?.id} className="flex items-start gap-3">
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                    style={{ backgroundColor: change?.userColor }}
                  >
                    {change?.userName?.split(' ')?.map(n => n?.[0])?.join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{change?.userName}</span>
                      {change?.type === 'edit' && ' edited text'}
                      {change?.type === 'comment' && ' added a comment'}
                      {change?.type === 'suggestion' && ' suggested changes'}
                    </p>
                    <p className="text-xs text-muted-foreground">{formatTime(change?.timestamp)}</p>
                    {change?.preview && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">
                        "{change?.preview}"
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          <div className="flex-1 p-4 overflow-y-auto">
            <h4 className="font-medium text-foreground mb-3">
              Suggestions ({suggestions?.length})
            </h4>
            <div className="space-y-3">
              {suggestions?.map((suggestion) => (
                <div key={suggestion?.id} className="border border-border rounded-lg p-3">
                  <div className="flex items-start gap-2 mb-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0"
                      style={{ backgroundColor: suggestion?.userColor }}
                    >
                      {suggestion?.userName?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{suggestion?.userName}</p>
                      <p className="text-xs text-muted-foreground">{formatTime(suggestion?.timestamp)}</p>
                    </div>
                  </div>

                  <div className="mb-3">
                    <p className="text-sm text-foreground mb-1">{suggestion?.comment}</p>
                    {suggestion?.originalText && (
                      <div className="text-xs">
                        <p className="text-error line-through">{suggestion?.originalText}</p>
                        <p className="text-success">{suggestion?.suggestedText}</p>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAcceptSuggestion(suggestion?.id)}
                      className="text-success hover:text-success"
                    >
                      <Icon name="Check" size={14} className="mr-1" />
                      Accept
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onRejectSuggestion(suggestion?.id)}
                      className="text-error hover:text-error"
                    >
                      <Icon name="X" size={14} className="mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-popover border border-border rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-popover-foreground">Invite Collaborator</h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowInviteModal(false)}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-popover-foreground mb-2 block">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e?.target?.value)}
                  placeholder="colleague@company.com"
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-popover-foreground mb-2 block">
                  Permission Level
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e?.target?.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="viewer">Viewer - Can view only</option>
                  <option value="commenter">Commenter - Can view and comment</option>
                  <option value="editor">Editor - Can view, comment, and edit</option>
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  variant="default"
                  onClick={handleInvite}
                  disabled={!inviteEmail?.trim()}
                  className="flex-1"
                >
                  Send Invitation
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteModal(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CollaborationPanel;